import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Gauge, Layers, X, GripVertical, Check } from "lucide-react";
import ProtocolInfo from "@/components/ProtocolInfo";

function PatientTestSelector() {
  const nav = useNavigate();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/patients")
      .then((r) => setPatients(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);
  const term = q.trim().toLowerCase();
  const hits = term
    ? patients.filter((p) => `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(term)).slice(0, 8)
    : [];
  return (
    <div className="fl-card p-4 mb-6" data-testid="pt-patient-selector">
      <div className="fl-label mb-2">Open a patient's Physical Tests workspace</div>
      <input className="fl-input w-full text-sm" placeholder="Search your patients by name or email…"
             value={q} onChange={(e) => setQ(e.target.value)} data-testid="pt-patient-search" />
      {hits.length > 0 && (
        <div className="mt-2 border border-white/10 rounded-lg divide-y divide-white/5 max-h-64 overflow-auto">
          {hits.map((p) => (
            <button key={p.id} onClick={() => nav(`/clinician/patient/${p.id}?tab=tests`)}
                    className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between"
                    data-testid={`pt-patient-opt-${p.id}`}>
              <span className="text-sm text-white truncate">{p.name || p.email}</span>
              <span className="text-[11px] text-[#f47a20]">Open →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_DEF = {
  name: "", category: "rom_mobility", body_region: "", measurement_type: "",
  unit: "", sided_mode: "bilateral", instructions: "", protocol: "", equipment: "",
  target_value: "", target_comparator: "gte", interpretation_direction: "neutral",
  safety_considerations: "",
};

// Mirrors the backend gate (_PT_APPROVAL_REQUIRED) so the UI can show the
// clinician exactly which mandatory clinical fields still block approval.
const PT_APPROVAL_REQUIRED = [
  ["name", "Name"], ["category", "Category"], ["measurement_type", "Measurement type"],
  ["unit", "Unit"], ["instructions", "Instructions"], ["protocol", "Protocol"],
  ["safety_considerations", "Safety considerations"],
];
function ptMissingFields(d) {
  return PT_APPROVAL_REQUIRED.filter(([k]) => {
    const v = d[k];
    return v == null || (typeof v === "string" && !v.trim());
  }).map(([, label]) => label);
}

const PT_STATUS_BADGE = {
  draft: { label: "Draft", cls: "border-amber-500/40 text-amber-400 bg-amber-500/10" },
  approved: { label: "Approved", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  archived: { label: "Archived", cls: "border-zinc-500/40 text-zinc-400 bg-zinc-500/10" },
};

// Defined at module scope so it is not re-created on every render
// (an inner component would remount its children and steal input focus).
const Fld = ({ label, children }) => (
  <div><div className="fl-label mb-1">{label}</div>{children}</div>
);

function DefinitionForm({ categories, initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial || EMPTY_DEF);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim() || !f.measurement_type.trim() || !f.unit.trim()) {
      toast.error("Name, measurement type and unit are required.");
      return;
    }
    if (f.target_value !== "" && f.target_value != null && !f.target_comparator) {
      toast.error("Choose a target comparator when a target value is set.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        body_region: f.body_region || null,
        instructions: f.instructions || null,
        protocol: f.protocol || null,
        equipment: f.equipment || null,
        safety_considerations: f.safety_considerations || null,
        target_value: f.target_value === "" || f.target_value == null ? null : parseFloat(f.target_value),
        target_comparator: (f.target_value === "" || f.target_value == null) ? null : f.target_comparator,
      };
      if (initial?.id) await api.patch(`/physical-tests/definitions/${initial.id}`, payload);
      else await api.post("/physical-tests/definitions", payload);
      toast.success(initial?.id ? "Test definition updated." : "Test definition created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const inp = "fl-input w-full text-sm px-2 py-2 rounded-md";

  return (
    <div className="fl-card p-5 mb-4 border border-[#f47a20]/30" data-testid="def-form">
      <div className="flex items-center justify-between mb-3">
        <div className="fl-heading text-lg">{initial?.id ? "Edit test definition" : "New test definition"}</div>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white" data-testid="def-form-close"><X size={18} /></button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Fld label="Name *"><input className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="def-name" /></Fld>
        <Fld label="Category *">
          <select className={inp} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="def-category">
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Fld>
        <Fld label="Body region (optional)"><input className={inp} value={f.body_region} onChange={(e) => set("body_region", e.target.value)} data-testid="def-body-region" placeholder="e.g. ankle, shoulder" /></Fld>
        <Fld label="Measurement type *"><input className={inp} value={f.measurement_type} onChange={(e) => set("measurement_type", e.target.value)} data-testid="def-measurement-type" placeholder="distance / time / force / angle / count / score" /></Fld>
        <Fld label="Unit *"><input className={inp} value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="def-unit" placeholder="cm, s, N, °, reps, %" /></Fld>
        <Fld label="Measured side">
          <select className={inp} value={f.sided_mode} onChange={(e) => set("sided_mode", e.target.value)} data-testid="def-sided-mode">
            <option value="bilateral">Bilateral (single value)</option>
            <option value="unilateral">Unilateral (left &amp; right)</option>
            <option value="either">Either</option>
          </select>
        </Fld>
        <Fld label="Interpretation">
          <select className={inp} value={f.interpretation_direction} onChange={(e) => set("interpretation_direction", e.target.value)} data-testid="def-interpretation">
            <option value="neutral">Neutral / context-dependent</option>
            <option value="higher_better">Higher is better</option>
            <option value="lower_better">Lower is better</option>
          </select>
        </Fld>
        <Fld label="Clinician target (optional)"><input type="number" step="any" className={inp} value={f.target_value} onChange={(e) => set("target_value", e.target.value)} data-testid="def-target-value" /></Fld>
        <Fld label="Target comparator">
          <select className={inp} value={f.target_comparator} onChange={(e) => set("target_comparator", e.target.value)} data-testid="def-target-comparator">
            <option value="gte">≥ at least</option>
            <option value="lte">≤ at most</option>
          </select>
        </Fld>
        <Fld label="Equipment"><input className={inp} value={f.equipment} onChange={(e) => set("equipment", e.target.value)} data-testid="def-equipment" /></Fld>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mt-3">
        <Fld label="Instructions"><textarea rows={2} className={inp} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="def-instructions" /></Fld>
        <Fld label="Protocol"><textarea rows={2} className={inp} value={f.protocol} onChange={(e) => set("protocol", e.target.value)} data-testid="def-protocol" placeholder="e.g. best of 3" /></Fld>
        <Fld label="Safety considerations"><textarea rows={2} className={inp} value={f.safety_considerations} onChange={(e) => set("safety_considerations", e.target.value)} data-testid="def-safety" /></Fld>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="fl-btn text-sm" data-testid="def-save">{busy ? "Saving…" : "Save definition"}</button>
        <button onClick={onCancel} className="fl-btn-ghost text-sm" data-testid="def-cancel">Cancel</button>
      </div>
    </div>
  );
}

function BatteryForm({ definitions, initial, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [ids, setIds] = useState(initial?.test_ids || []);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const move = (i, dir) => setIds((s) => {
    const n = [...s]; const j = i + dir;
    if (j < 0 || j >= n.length) return s;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const save = async () => {
    if (!name.trim()) { toast.error("Battery name is required."); return; }
    if (ids.length === 0) { toast.error("Select at least one test."); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description: description || null, test_ids: ids };
      if (initial?.id) await api.patch(`/physical-tests/batteries/${initial.id}`, payload);
      else await api.post("/physical-tests/batteries", payload);
      toast.success(initial?.id ? "Battery updated." : "Battery created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const byId = Object.fromEntries(definitions.map((d) => [d.id, d]));
  const inp = "fl-input w-full text-sm px-2 py-2 rounded-md";

  return (
    <div className="fl-card p-5 mb-4 border border-[#f47a20]/30" data-testid="battery-form">
      <div className="flex items-center justify-between mb-3">
        <div className="fl-heading text-lg">{initial?.id ? "Edit battery" : "New assessment battery"}</div>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white" data-testid="battery-form-close"><X size={18} /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><div className="fl-label mb-1">Name *</div><input className={inp} value={name} onChange={(e) => setName(e.target.value)} data-testid="battery-name" /></div>
        <div><div className="fl-label mb-1">Description</div><input className={inp} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="battery-description" /></div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <div className="fl-label mb-2">Available tests</div>
          <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
            {definitions.length === 0 && <div className="text-zinc-500 text-sm">No test definitions yet — create one first.</div>}
            {definitions.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm text-zinc-300 py-1 cursor-pointer" data-testid={`battery-pick-${d.id.slice(0, 8)}`}>
                <input type="checkbox" checked={ids.includes(d.id)} onChange={() => toggle(d.id)} />
                <span>{d.name} <span className="text-zinc-500">· {d.unit}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className="fl-label mb-2">Selected &amp; order ({ids.length})</div>
          <div className="space-y-1">
            {ids.map((id, i) => (
              <div key={id} className="flex items-center gap-2 text-sm bg-white/5 rounded px-2 py-1.5" data-testid={`battery-order-${id.slice(0, 8)}`}>
                <GripVertical size={12} className="text-zinc-500" />
                <span className="flex-1 text-zinc-200">{byId[id]?.name || id}</span>
                <button onClick={() => move(i, -1)} className="text-zinc-400 hover:text-white px-1">↑</button>
                <button onClick={() => move(i, 1)} className="text-zinc-400 hover:text-white px-1">↓</button>
                <button onClick={() => toggle(id)} className="text-red-400 hover:text-red-300 px-1"><X size={12} /></button>
              </div>
            ))}
            {ids.length === 0 && <div className="text-zinc-500 text-sm">Pick tests from the left.</div>}
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="fl-btn text-sm" data-testid="battery-save">{busy ? "Saving…" : "Save battery"}</button>
        <button onClick={onCancel} className="fl-btn-ghost text-sm" data-testid="battery-cancel">Cancel</button>
      </div>
    </div>
  );
}

export default function ClinicianPhysicalTests() {
  const { user } = useAuth();
  const isClinicalAdmin = (user?.role === "admin" || !!user?.is_admin) && !!user?.clinical_admin;
  const [tab, setTab] = useState("definitions");
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | approved | archived
  const [categories, setCategories] = useState([]);
  const [defs, setDefs] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [editingDef, setEditingDef] = useState(null);   // def object or {} for new
  const [editingBat, setEditingBat] = useState(null);

  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;

  const load = () => {
    api.get("/physical-tests/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    const params = statusFilter === "all" ? "?include_archived=true" : `?status=${statusFilter}`;
    api.get(`/physical-tests/definitions${params}`).then((r) => setDefs(r.data || [])).catch(() => {});
    api.get("/physical-tests/batteries").then((r) => setBatteries(r.data || [])).catch(() => {});
  };
  useEffect(load, [statusFilter]); // eslint-disable-line

  const approveDef = async (d) => {
    try {
      await api.post(`/physical-tests/definitions/${d.id}/approve`);
      toast.success(`"${d.name}" approved & activated`);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && det.missing_fields) toast.error(`Complete before approval: ${det.missing_fields.join(", ")}`);
      else toast.error(typeof det === "string" ? det : (det?.message || "Approval failed"));
    }
  };

  const archiveDef = async (id) => {
    if (!window.confirm("Archive this test definition? Existing results are preserved.")) return;
    await api.delete(`/physical-tests/definitions/${id}`);
    toast.success("Archived."); load();
  };
  const archiveBat = async (id) => {
    if (!window.confirm("Archive this battery?")) return;
    await api.delete(`/physical-tests/batteries/${id}`);
    toast.success("Archived."); load();
  };

  return (
    <DashLayout>
      <div className="mb-6">
        <div className="fl-label text-[#f47a20]">Universal assessment</div>
        <h1 className="fl-heading text-4xl mt-2" data-testid="pt-library-h1">Physical Tests</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Create reusable, universal test definitions and combine them into assessment batteries for any patient or healthy athlete. Targets are clinician-defined — no normative values are assumed.</p>
      </div>

      <PatientTestSelector />

      <div role="tablist" className="flex gap-2 mb-6 border-b border-white/10">
        {[["definitions", "Test definitions", Gauge], ["batteries", "Batteries", Layers]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`pt-tab-${k}`}
            className={`px-4 py-2 text-sm transition-colors ${tab === k ? "text-[#f47a20] border-b-2 border-[#f47a20] -mb-px" : "text-zinc-400 hover:text-white"}`}>
            <Icon size={14} className="inline mr-2" /> {label}
          </button>
        ))}
      </div>

      {tab === "definitions" && (
        <div data-testid="pt-definitions-panel">
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="pt-status-filter">
            {[["all", "All"], ["draft", "Draft"], ["approved", "Approved"], ["archived", "Archived"]].map(([val, label]) => (
              <button key={val} type="button" data-testid={`pt-filter-${val}`}
                onClick={() => setStatusFilter(val)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === val
                    ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10"
                    : "border-white/10 text-zinc-400 hover:text-zinc-200"
                }`}>
                {label}
              </button>
            ))}
          </div>
          {!editingDef && (
            <button onClick={() => setEditingDef({})} className="fl-btn text-sm mb-4 inline-flex items-center gap-1" data-testid="def-new-btn"><Plus size={14} /> New test definition</button>
          )}
          {editingDef && (
            <DefinitionForm categories={categories} initial={editingDef.id ? editingDef : null}
              onSaved={() => { setEditingDef(null); load(); }} onCancel={() => setEditingDef(null)} />
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {defs.map((d) => {
              const st = d.clinical_status || "draft";
              const badge = PT_STATUS_BADGE[st] || PT_STATUS_BADGE.draft;
              const missing = ptMissingFields(d);
              const mine = d.created_by_id === user?.id;
              return (
              <div key={d.id} className="fl-card p-4" data-testid={`def-card-${d.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="fl-heading text-base">{d.name}</div>
                      <ProtocolInfo def={d} />
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${badge.cls}`} data-testid={`def-status-${d.id.slice(0, 8)}`}>{badge.label}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">{catLabel(d.category)} · {d.unit} · {d.sided_mode}{d.body_region ? ` · ${d.body_region}` : ""}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingDef(d)} className="text-zinc-400 hover:text-[#f47a20]" data-testid={`def-edit-${d.id.slice(0, 8)}`}><Pencil size={14} /></button>
                    {st !== "archived" && (
                      <button onClick={() => archiveDef(d.id)} className="text-zinc-400 hover:text-red-400" data-testid={`def-archive-${d.id.slice(0, 8)}`}><Archive size={14} /></button>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  <span>v{d.version}</span>
                  {d.interpretation_direction !== "neutral" && <span>{d.interpretation_direction === "higher_better" ? "↑ better" : "↓ better"}</span>}
                  {d.target_value != null && <span>target {d.target_comparator === "gte" ? "≥" : "≤"} {d.target_value}</span>}
                  {d.protocol && <span>· {d.protocol}</span>}
                  {st === "approved" && d.approved_by_name && <span>· approved by {d.approved_by_name}</span>}
                </div>
                {d.safety_considerations && <div className="text-[11px] text-amber-400/80 mt-1">⚠ {d.safety_considerations}</div>}

                {st === "draft" && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    {missing.length > 0 && (
                      <div className="text-[10px] text-amber-400 mb-1" data-testid={`def-missing-${d.id.slice(0, 8)}`}>
                        Complete before approval: {missing.join(", ")}
                      </div>
                    )}
                    {isClinicalAdmin ? (
                      <>
                        <button
                          className="fl-btn text-xs w-full justify-center inline-flex items-center gap-1"
                          data-testid={`def-approve-${d.id.slice(0, 8)}`}
                          disabled={mine || missing.length > 0}
                          title={mine
                            ? "You created this draft — a different clinical reviewer must sign it off"
                            : (missing.length ? "Complete the mandatory clinical fields first" : "Approve & activate this physical test")}
                          onClick={() => approveDef(d)}
                        >
                          <Check size={12} /> Approve &amp; Activate
                        </button>
                        {mine && (
                          <div className="text-[10px] text-zinc-500 mt-1" data-testid={`def-sod-note-${d.id.slice(0, 8)}`}>
                            You created this draft — a different clinical reviewer must sign it off.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-[10px] text-zinc-500" data-testid={`def-review-pending-${d.id.slice(0, 8)}`}>
                        Awaiting clinical review before it can be used.
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            {defs.length === 0 && !editingDef && <div className="text-zinc-500 text-sm">No test definitions in this view.</div>}
          </div>
        </div>
      )}

      {tab === "batteries" && (
        <div data-testid="pt-batteries-panel">
          {!editingBat && (
            <button onClick={() => setEditingBat({})} className="fl-btn text-sm mb-4 inline-flex items-center gap-1" data-testid="battery-new-btn"><Plus size={14} /> New battery</button>
          )}
          {editingBat && (
            <BatteryForm definitions={defs} initial={editingBat.id ? editingBat : null}
              onSaved={() => { setEditingBat(null); load(); }} onCancel={() => setEditingBat(null)} />
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {batteries.map((b) => (
              <div key={b.id} className="fl-card p-4" data-testid={`battery-card-${b.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="fl-heading text-base">{b.name}</div>
                    {b.description && <div className="text-xs text-zinc-400 mt-0.5">{b.description}</div>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingBat(b)} className="text-zinc-400 hover:text-[#f47a20]" data-testid={`battery-edit-${b.id.slice(0, 8)}`}><Pencil size={14} /></button>
                    <button onClick={() => archiveBat(b.id)} className="text-zinc-400 hover:text-red-400" data-testid={`battery-archive-${b.id.slice(0, 8)}`}><Archive size={14} /></button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(b.tests || []).map((t) => (
                    <span key={t.id} className="text-[11px] bg-white/5 text-zinc-300 rounded px-2 py-0.5">{t.name}</span>
                  ))}
                  {(b.tests || []).length === 0 && <span className="text-[11px] text-zinc-500">No active tests</span>}
                </div>
              </div>
            ))}
            {batteries.length === 0 && !editingBat && <div className="text-zinc-500 text-sm">No batteries yet.</div>}
          </div>
        </div>
      )}
    </DashLayout>
  );
}
